import React from 'react';
import { 
  X, BookOpen, ChevronLeft, ChevronRight 
} from 'lucide-react';

/**
 * Panneau de détails des concepts pour SmartPDFViewer
 */
const ConceptsPanel = ({
  selectedConcept,
  onClose,
  onNavigateToPage,
  onNavigateToPrevConceptPassage,
  onNavigateToNextConceptPassage,
  conceptPassages = []
}) => {
  if (!selectedConcept) return null;

  // Déterminer la source du concept
  const sourceLabel = selectedConcept.searchOrigin 
    ? `Issu de la recherche: "${selectedConcept.searchQuery}"` 
    : "Concept identifié dans l'analyse du document";

  // Calculer l'index et le nombre total de passages pour ce concept
  const conceptPassageIndex = conceptPassages.findIndex(
    p => p.id === selectedConcept.id
  );

  return (
    <div className="p-4 bg-white border-l border-gray-200 overflow-y-auto w-full h-full flex flex-col">
      {/* En-tête du panneau */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          {selectedConcept.concept}
          <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-medium ${
            selectedConcept.searchOrigin 
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {selectedConcept.searchOrigin ? 'Recherche' : 'Concept'}
          </span>
        </h3>
        
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Contenu du panneau */}
      <div className="space-y-4 flex-grow overflow-auto">
        {/* Source du concept */}
        <div className="text-sm text-gray-500 italic">
          {sourceLabel}
        </div>
        
        {/* Description générale */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
            {selectedConcept.description || "Aucune description disponible."}
          </div>
        </div>
        
        {/* Résumé contextuel */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Application dans ce document</h4>
          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
            {selectedConcept.resume || "Aucun résumé disponible."}
          </div>
        </div>
        
        {/* Extraits du document */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">
            Extraits du document
            {selectedConcept.pages && <span className="text-gray-500 ml-1">(Page {selectedConcept.pages})</span>}
          </h4>
          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 border-l-2 border-blue-400">
            {selectedConcept.extraits || selectedConcept.content || "Aucun extrait disponible."}
          </div>
        </div>
      </div>
      
      {/* Navigation et actions */}
      <div className="mt-4 pt-3 border-t border-gray-200 flex flex-col space-y-2">
        {/* Navigation entre passages du même concept */}
        {conceptPassages.length > 1 && (
          <div className="flex justify-between items-center">
            <button
              onClick={onNavigateToPrevConceptPassage}
              className="p-2 rounded-full hover:bg-gray-100 flex items-center"
              aria-label="Passage précédent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <span className="text-xs text-gray-500">
              Passage {conceptPassageIndex + 1} / {conceptPassages.length}
            </span>
            
            <button
              onClick={onNavigateToNextConceptPassage}
              className="p-2 rounded-full hover:bg-gray-100 flex items-center"
              aria-label="Passage suivant"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {/* Bouton de navigation vers la page */}
        <button 
          onClick={() => {
            onNavigateToPage(parseInt(selectedConcept.pages) || selectedConcept.pageNumber || 1);
            onClose();
          }}
          className="w-full py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Aller à la page {selectedConcept.pages || selectedConcept.pageNumber || 1}
        </button>
      </div>
    </div>
  );
};

export default ConceptsPanel;